export interface SubjectConfig {
  /** Identificador interno estable para la asignatura */
  id: string;
  /** Nombre visible para el usuario */
  name: string;
  /** Código que usa la API (por ejemplo, TSO, DCA, etc.) */
  code: string;
  /** Icono Remix Icon para la UI */
  icon: string;
  /** Gradiente principal de color para la tarjeta (dark-theme safe) */
  color: string;
  /** Color de acento sólido para bordes, brillos y badges */
  accentColor: string;
  /** Descripción breve de la asignatura */
  description: string;
  /** Palabras clave adicionales para la búsqueda (sin distinción de mayúsculas/minúsculas) */
  keywords: string[];
  /** Contraseña de acceso por asignatura (bloqueo en cliente) */
  accessPassword?: string;
}

export const SUBJECTS: SubjectConfig[] = [
  {
    id: 'tso',
    name: 'Teatro del Siglo de Oro',
    code: 'TSO',
    icon: 'ri-quill-pen-line',
    color: 'from-[#8B6914] to-[#C9A854]',
    accentColor: '#C9A854',
    description: 'Teatro clásico español del período barroco',
    keywords: ['teatro', 'siglo de oro', 'literatura', 'barroco'],
    accessPassword: 'TSO-2026',
  },
  {
    id: 'dca',
    name: 'Desarrollo Colaborativo de Aplicaciones',
    code: 'DCA',
    icon: 'ri-code-s-slash-line',
    color: 'from-[#1E5F8A] to-[#3A9ED4]',
    accentColor: '#3A9ED4',
    description: 'Metodologías de desarrollo software en equipo',
    keywords: ['desarrollo', 'colaborativo', 'aplicaciones', 'software'],
    accessPassword: 'DCA-2026',
  },
  {
    id: 'leir',
    name: 'Literatura Española de la Ilustración y del Romanticismo',
    code: 'LEIR',
    icon: 'ri-book-open-line',
    color: 'from-[#7A4D2A] to-[#C97D49]',
    accentColor: '#C97D49',
    description: 'Literatura de la Ilustración y el Romanticismo',
    keywords: ['literatura', 'española', 'ilustracion', 'romanticismo'],
    accessPassword: 'LEIR-2026',
  },
  {
    id: 'algor',
    name: 'Algoritmia Grado en Inteligencia Artificial',
    code: 'ALGOR',
    icon: 'ri-cpu-line',
    color: 'from-[#2E6B4F] to-[#7A9E8E]',
    accentColor: '#7A9E8E',
    description: 'Algoritmos y estructuras de datos para IA',
    keywords: ['algoritmia', 'analisis', 'diseño', 'programacion'],
    accessPassword: 'ALGOR-2026',
  },
  {
    id: 'p2_gir',
    name: 'Programación 2 Grado en Ingeniería Robótica',
    code: 'P2_GIR',
    icon: 'ri-robot-line',
    color: 'from-[#4A2A7A] to-[#8B5CF6]',
    accentColor: '#8B5CF6',
    description: 'Programación avanzada para ingeniería robótica',
    keywords: ['programacion', 'c++', 'ingenieria', 'robotica'],
    accessPassword: 'P2_GIR-2026',
  },
  {
    id: 'dreh',
    name: 'Derechos Reales e Hipotéticos',
    code: 'DREH',
    icon: 'ri-scales-3-line',
    color: 'from-[#6B2A3A] to-[#E07090]',
    accentColor: '#E07090',
    description: 'Derechos reales, garantías e hipotecas',
    keywords: ['derecho', 'real', 'hipotetico'],
    accessPassword: 'DREH-2026',
  },
  {
    id: 'hetc',
    name: 'Historia del Espectáculo: Teatro y Cine en la España Contemporánea',
    code: 'HETC',
    icon: 'ri-clapperboard-line',
    color: 'from-[#1F5A5A] to-[#4FB3A9]',
    accentColor: '#4FB3A9',
    description: 'Historia del teatro y el cine en la España contemporánea',
    keywords: ['historia', 'espectaculo', 'teatro', 'cine', 'contemporanea'],
    accessPassword: 'HETC-2026',
  },
];

/** Devuelve la configuración de una asignatura a partir de su nombre visible. */
export function getSubjectByName(name: string): SubjectConfig | undefined {
  return SUBJECTS.find(
    (s) => s.name.toLowerCase() === name.trim().toLowerCase()
  );
}

/** Devuelve la configuración de una asignatura a partir de su código de API. */
export function getSubjectByCode(code: string): SubjectConfig | undefined {
  return SUBJECTS.find(
    (s) => s.code.toLowerCase() === code.trim().toLowerCase()
  );
}

/** Dado un nombre de asignatura, devuelve el código que espera la API. */
export function getSubjectCodeFromName(subjectName: string): string {
  const match = getSubjectByName(subjectName);
  return match?.code ?? subjectName;
}

/** Búsqueda de asignaturas por nombre o keywords. */
export function searchSubjects(term: string): SubjectConfig[] {
  const normalized = term.trim().toLowerCase();
  if (!normalized) {
    return SUBJECTS;
  }

  return SUBJECTS.filter((subject) => {
    const inName = subject.name.toLowerCase().includes(normalized);
    const inKeywords = subject.keywords.some((k) =>
      k.toLowerCase().includes(normalized)
    );
    return inName || inKeywords;
  });
}
